import { HttpApiService } from "@/shared/services/httpApi.service";
import type { Permission } from "@/shared/types/permission.types";

export interface AllowedIp {
  ip: string;
  label?: string;
}

export interface ApiKeyEntity {
  id: string;
  name: string;
  key: string;
  scopes: Permission[];
  is_admin: boolean;
  super_admin: boolean;
  is_active: boolean;
  expires_at?: string | null;
  rate_limit?: number | null;
  usage_count: number;
  last_used_at?: string | null;
  allowed_ips: AllowedIp[];
  organization_id?: string | null;
  created_at: string;
  updated_at: string;
}

interface PaginatedApiKeys {
  data: ApiKeyEntity[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

export interface CreateApiKeyRequest {
  name: string;
  scopes: Permission[];
  allowed_ips?: AllowedIp[];
}

/** Response of POST /api-keys — `plainKey` is the full secret, returned only once. */
export interface CreateApiKeyResponse {
  id: string;
  name: string;
  plainKey: string;
  scopes: Permission[];
  is_admin: boolean;
  super_admin: boolean;
  created_at: string;
}

export const apiKeysService = {
  getApiKeys: async (skip = 0, limit = 50): Promise<PaginatedApiKeys> => {
    const response = await HttpApiService.get<PaginatedApiKeys>("/api-keys", {
      params: { skip, limit },
    });
    return response.data;
  },

  createApiKey: async (body: CreateApiKeyRequest): Promise<CreateApiKeyResponse> => {
    const response = await HttpApiService.post<CreateApiKeyResponse, CreateApiKeyRequest>("/api-keys", body);
    return response.data;
  },

  disableApiKey: async (id: string): Promise<ApiKeyEntity> => {
    const response = await HttpApiService.patch<ApiKeyEntity, { is_active: boolean }>(`/api-keys/${id}`, {
      is_active: false,
    });
    return response.data;
  },

  enableApiKey: async (id: string): Promise<ApiKeyEntity> => {
    const response = await HttpApiService.patch<ApiKeyEntity, { is_active: boolean }>(`/api-keys/${id}`, {
      is_active: true,
    });
    return response.data;
  },

  updateAllowedIps: async (id: string, allowedIps: AllowedIp[]): Promise<ApiKeyEntity> => {
    const response = await HttpApiService.patch<ApiKeyEntity, { allowed_ips: AllowedIp[] }>(
      `/api-keys/${id}`,
      { allowed_ips: allowedIps },
    );
    return response.data;
  },

  getCurrentIp: async (): Promise<string | null> => {
    try {
      const response = await HttpApiService.get<{ ip: string | null }>("/auth/whoami/ip");
      const fromBackend = response.data.ip;
      if (fromBackend && fromBackend !== "unknown") return fromBackend;
    } catch {
      // fall through to public service
    }
    try {
      const res = await fetch("https://api.ipify.org?format=json", {
        signal: AbortSignal.timeout(3000),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { ip?: string };
      return data.ip ?? null;
    } catch {
      return null;
    }
  },
};
